/* eslint-disable indent */
import GigFreelancer from './GigFreelancer'
import { cloneDeep } from 'lodash'
import omit from 'lodash/omit'
import sortBy from 'lodash/sortBy'
import unescape from 'lodash/unescape'
import { FileCategories } from '@/constants/backend/FileCategories'
import { ModerationStages } from '@/constants/backend/ModerationStages'
import { Blockchain } from '@/constants/blockchain'
import { GigTimeTypes } from '@/constants/gig/gigTimeTypes'
import File from '@/models-ts/File'
import Image from '@/models-ts/Image'
import { parseSlug } from '@/utils/parser'
import GigListItem from '../gigs/GigListItem'
import Skill from '../Skill'

interface Relations {
  Banner: Array<LX.Image | Image>
  Currency: Array<{
    blockchain: Blockchain
    currency: number
  }>
  Freelancer: GigFreelancer
  File: Array<File>
  Image: Array<LX.Image | Image>
  Skill: Array<Skill>
}

export default class Gig {
  id: number
  slug: string
  user_id: number
  user_name: string | null
  name: string
  description: string
  rate: string
  status: number
  moderationStage: ModerationStages
  time_type: GigTimeTypes
  time_value: string
  offerStage: number | null
  createdAt: string | null
  publishedAt: string | null
  views: number | null
  isSeoVisible: boolean
  relations!: Relations
  meta: {
    completedContractsCount?: number
    category: Skill
  }
  more: {
    gigs: {
      by_user: Array<GigListItem>
      by_category: Array<GigListItem>
    }
  }

  constructor(data?: Partial<Gig>) {
    const obj = data || {}
    Object.assign(
      this,
      cloneDeep({
        id: obj.id,
        slug: obj.slug || '',
        user_id: obj.user_id,
        user_name: obj.user_name || '',
        name: obj.name || '',
        description: obj.description || '',
        rate: obj.rate || '0.00',
        status: obj.status,
        moderationStage: obj.moderationStage,
        time_type: obj.time_type || GigTimeTypes.FIXED,
        time_value: obj.time_type || '0',
        offerStage: obj.offerStage || null,
        createdAt: obj.createdAt || null,
        publishedAt: obj.publishedAt || null,
        views: obj.views || 0,
        isSeoVisible: 'isSeoVisible' in obj ? obj.isSeoVisible : true,
        meta: obj.meta || {},
        more: obj.more || {},
        relations: {
          Banner: obj.relations?.Banner || [],
          Currency: obj.relations?.Currency || [],
          Freelancer: obj.relations?.Freelancer || {},
          File: obj.relations?.File || [],
          Image: obj.relations?.Image || [],
          Skill: obj.relations?.Skill || [],
        },
      })
    )
  }

  static fromServer(data?: LX.Gig) {
    if (!data) return null
    const offers = data?.relations.Offer || []
    return data
      ? new Gig({
          ...data,
          isSeoVisible: data.is_seo_visible === 1,
          createdAt: data.created_at,
          publishedAt: data.first_published_at,
          slug: parseSlug(data.slug),
          name: unescape(data.name),
          time_value: String(data.time_value || 0),
          rate: data.rate ? String(Number(data.rate).toFixed(2)) : '0.00',
          offerStage: offers.length ? offers[offers.length - 1].stage : null,
          moderationStage: data.moderation_stage || ModerationStages.PASSED,
          meta: {
            completedContractsCount: data?.meta?.completedContractsCount || 0,
            category: Skill.fromServer(data.meta?.category),
          },
          more: {
            gigs: {
              by_user: (data.more.gigs.by_user || []).map(GigListItem.fromServer),
              by_category: (data.more.gigs.by_category || []).map(GigListItem.fromServer),
            },
          },
          relations: {
            Banner: data.relations?.Banner || [],
            Currency: sortBy(data.relations?.Currency, 'blockchain').map((cur) => ({
              blockchain: cur.blockchain,
              currency: cur.currency,
            })),
            Freelancer: GigFreelancer.fromServer(data.relations?.Freelancer),
            File: (data.relations?.File || []).map(File.fromServer),
            Image: data.relations?.Image || [],
            Skill: (data.relations?.Skill || []).map(Skill.fromServer),
          },
        })
      : null
  }

  get banner() {
    if (this.relations.Banner.length < 1) return null
    const hasNoImages = this.relations.Banner.every((b) => !(b instanceof Image))
    if (hasNoImages) {
      return Image.fromServer(this.relations.Banner as Array<LX.Image>, { category: FileCategories.CAT_IMG_CROPPED })
    }
    return this.relations.Banner.length > 0 ? this.relations.Banner[0] : null
  }

  get images() {
    const nUploaded = this.relations.Image.filter((i) => i instanceof Image)
    const uploaded = this.relations.Image.filter((i) => !(i instanceof Image)) as Array<LX.Image>
    const images = Image.arrayFromServer(uploaded, { category: FileCategories.CAT_IMG_CROPPED })
    return nUploaded.concat(images)
  }

  static toServerCreate(data: Gig) {
    const images = data.relations.Image.map((i) => (i instanceof Image ? i.toServer()[0] : i))
    const banner = data.relations.Banner.map((i) => (i instanceof Image ? i.toServer()[0] : i))
    return {
      ...data,
      relations: {
        ...data.relations,
        Image: images,
        Banner: banner,
      },
    }
  }

  static toServerUpdate(data: Gig) {
    const images = data.relations.Image.map((i) => (i instanceof Image ? i.toServer()[0] : i))
    const banner = data.relations.Banner.map((i) => (i instanceof Image ? i.toServer()[0] : i))
    return {
      ...omit(
        {
          ...data,
          relations: {
            ...data.relations,
            Image: images,
            Banner: banner,
          },
        },
        ['id', 'status', 'relations.Freelancer', 'moderationStage']
      ),
    }
  }
}
